import { useCallback, useState } from 'react';
import { apiClient } from '../api/client';
import { useToast } from '../components/ui/Toast';
import { useAppMode } from '../context/AppModeContext';
import { useAuth } from './useAuth';
import type { PdfLayout, PdfOrientation } from '../lib/pdfCatalog';
import type { InventoryItem } from '@siliconvault/shared';

async function downloadFromUrl(url: string, filename: string) {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) throw new Error(`Export failed: ${res.status}`);
  const blob = await res.blob();
  downloadBlob(blob, filename);
}

function downloadBlob(blob: Blob, filename: string) {
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export function useExport() {
  const { toast } = useToast();
  const { user } = useAuth();
  const isDesktop = useAppMode().mode === 'desktop';
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [pdfProgress, setPdfProgress] = useState<{ current: number; total: number } | null>(null);

  const exportCsv = useCallback((filters: Record<string, string> = {}) => {
    const url = apiClient.getExportCsvUrl(filters);
    downloadFromUrl(url, 'siliconvault-export.csv')
      .then(() => { if (isDesktop) toast('siliconvault-export.csv saved to your Downloads folder', 'success'); })
      .catch(() => toast('Export failed — please try again', 'error'));
  }, [toast, isDesktop]);

  const exportJson = useCallback((filters: Record<string, string> = {}) => {
    const url = apiClient.getExportJsonUrl(filters);
    downloadFromUrl(url, 'siliconvault-export.json')
      .then(() => { if (isDesktop) toast('siliconvault-export.json saved to your Downloads folder', 'success'); })
      .catch(() => toast('Export failed — please try again', 'error'));
  }, [toast, isDesktop]);

  const exportPdf = useCallback(async (filters: Record<string, string> = {}, layout: PdfLayout = 'compact', activeFilters?: string[], orientation: PdfOrientation = 'portrait') => {
    setPdfGenerating(true);
    setPdfProgress(null);
    try {
      // Fetch all items via the JSON export endpoint
      const url = apiClient.getExportJsonUrl(filters);
      const res = await fetch(url, { credentials: 'include' });
      if (!res.ok) throw new Error(`Export failed: ${res.status}`);
      const json = await res.json();
      // Web returns a flat array, desktop returns { data: [...], exportedAt: "..." }
      const items: InventoryItem[] = Array.isArray(json) ? json : json.data;

      // Dynamic import to lazy-load jsPDF
      const { generateCatalogPdf } = await import('../lib/pdfCatalog');

      const blob = await generateCatalogPdf({
        items,
        layout,
        orientation,
        generatedDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
        ownerName: user?.displayName || 'Collector',
        activeFilters,
        onProgress: (current, total) => setPdfProgress({ current, total }),
      });

      downloadBlob(blob, 'siliconvault-catalog.pdf');
      if (isDesktop) toast('siliconvault-catalog.pdf saved to your Downloads folder', 'success');
    } catch (err) {
      console.error('[PDF export] Failed:', err);
      toast('PDF export failed — please try again', 'error');
    } finally {
      setPdfGenerating(false);
      setPdfProgress(null);
    }
  }, [toast, isDesktop, user?.displayName]);

  return { exportCsv, exportJson, exportPdf, pdfGenerating, pdfProgress };
}
